/** The linter's diagnostic, rule and grounding types for `.tscn` validation. */
/**
 * Canonical severity ranking (lower is more severe): error, warning, info.
 * Every severity comparison reads it: `Linter`'s sort and every host that
 * ranks diagnostics, such as the web Source pane gutter.
 */
export const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };
/** Whether a string is one of the three tiers, the test before a table is indexed by severity. */
function isSeverity(value) {
    // `hasOwn`, not a bare index: `SEVERITY_ORDER['constructor']` reads
    // `Object.prototype`, and an unknown key yields `undefined`, which compares
    // false both ways and leaves a sort unordered.
    return Object.hasOwn(SEVERITY_ORDER, value);
}
/**
 * The severity itself, or `info` where the union does not hold it. `info`, not
 * a throw: every reader runs in an editor or webview with no error boundary,
 * and VS Code's default for an absent severity is Error, which over-reports.
 * Takes a `string`, since a caller with an off-union tier cannot pass `Severity`.
 */
export function flooredSeverity(value) {
    return isSeverity(value) ? value : 'info';
}
/**
 * The severity a grounding fixes, or `undefined` where only the cited line can
 * decide: an `engine` arm errors when the setter refuses or alters, and warns
 * for a hint or a load-time `WARN_PRINT`. `emitsGrounding.test.ts` holds every
 * emit to it.
 */
export function severityFixedBy(grounding) {
    // Total over both unions: a new kind or scope fails tsc in a `default` arm
    // instead of returning `undefined`, which callers read as "the cite decides".
    // The arm throws, since the unmatched value is not a `Severity`.
    switch (grounding.kind) {
        case 'engine':
            return undefined;
        case 'configuration-warning':
            return 'warning';
        case 'engine-inert':
            return 'info';
        case 'no-engine-counterpart':
            switch (grounding.scope) {
                case 'previewer-limitation':
                    return 'info';
                case 'linter-failure':
                    return 'error';
                case 'dangling-reference':
                case 'unresolvable-path':
                case 'file-integrity':
                    return 'warning';
                default: {
                    const unmatched = grounding.scope;
                    throw new Error(`no severity fixed for scope ${String(unmatched)}`);
                }
            }
        default: {
            const unmatched = grounding;
            throw new Error(`no severity fixed for grounding kind ${String(unmatched.kind)}`);
        }
    }
}
//# sourceMappingURL=types.js.map